// Timeline Report Page for AAR Guest Management System
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, Timestamp, documentId } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { canAccessReports } from '../utils/roles';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../styles/TimelineReport.css';

const TimelineReport = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [timeframe, setTimeframe] = useState('week');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState('');
  const [services, setServices] = useState(['all']);
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    getUserRole(user.uid).then(role => {
      if (!canAccessReports(role)) {
        navigate('/dashboard');
      } else {
        loadServices();
      }
    });
  }, [user, navigate]);

  const loadServices = async () => {
    try {
      // Predefined service categories
      const serviceCategories = [
        'all',
        '9am',
        '12pm',
        '3pm',
        'Tuesday Prayer',
        'Special Services'
      ];
      setServices(serviceCategories);
    } catch (err) {
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate report on page load and when filters change
  useEffect(() => {
    if (!loading && !generating) {
      generateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, timeframe, serviceFilter]);

  // Helper function to categorize service types
  const categorizeService = (serviceType, checkInTime) => {
    if (!serviceType) return 'Special Services';

    const serviceLower = serviceType.toLowerCase();

    // Check for specific Sunday service times
    if (serviceLower.includes('9') && serviceLower.includes('am')) return '9am';
    if (serviceLower.includes('12') && serviceLower.includes('pm')) return '12pm';
    if (serviceLower.includes('3') && serviceLower.includes('pm')) return '3pm';

    // Check for Tuesday Prayer
    if (serviceLower.includes('tuesday') || serviceLower.includes('prayer')) return 'Tuesday Prayer';

    // Get day of week from check-in time
    const date = checkInTime instanceof Date ? checkInTime : checkInTime.toDate();
    const dayOfWeek = date.getDay(); // 0 = Sunday, 2 = Tuesday

    // If it's Sunday (0) and matches service time patterns, categorize accordingly
    if (dayOfWeek === 0) {
      const hour = date.getHours();
      if (hour >= 8 && hour < 11) return '9am';
      if (hour >= 11 && hour < 14) return '12pm';
      if (hour >= 14 && hour < 17) return '3pm';
    }

    // If it's Tuesday (2), likely Tuesday Prayer
    if (dayOfWeek === 2) return 'Tuesday Prayer';

    // Everything else is Special Services
    return 'Special Services';
  };

  // Helper function to check if a service matches the selected filter
  const serviceMatchesFilter = (serviceType, checkInTime, filter) => {
    if (filter === 'all') return true;

    const category = categorizeService(serviceType, checkInTime);
    return category === filter;
  };

  const getTimeframeRange = (timeframe) => {
    const now = new Date();
    const start = new Date();

    switch (timeframe) {
      case 'day':
        start.setDate(now.getDate() - 1);
        break;
      case 'week':
        start.setDate(now.getDate() - 7);
        break;
      case 'month':
        start.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        start.setMonth(now.getMonth() - 3);
        break;
      case 'semi-annual':
        start.setMonth(now.getMonth() - 6);
        break;
      case 'annual':
        start.setFullYear(now.getFullYear() - 1);
        break;
      default:
        start.setDate(now.getDate() - 7);
    }

    return { start, end: now };
  };

  const getDateFormat = (timeframe) => {
    switch (timeframe) {
      case 'day':
        return { groupBy: 'hour', format: (date) => date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }) };
      case 'week':
        return { groupBy: 'day', format: (date) => date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) };
      case 'month':
        return { groupBy: 'day', format: (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
      case 'quarter':
      case 'semi-annual':
        return { groupBy: 'week', format: (date) => `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` };
      case 'annual':
        return { groupBy: 'month', format: (date) => date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) };
      default:
        return { groupBy: 'day', format: (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
    }
  };

  const generateTimeBuckets = (start, end, groupBy) => {
    const buckets = new Map();
    const current = new Date(start);

    // For week grouping, align start to Sunday to match getBucketKey
    if (groupBy === 'week') {
      current.setDate(current.getDate() - current.getDay());
    }

    while (current <= end) {
      let key;
      switch (groupBy) {
        case 'hour':
          key = new Date(current.getFullYear(), current.getMonth(), current.getDate(), current.getHours()).toISOString();
          current.setHours(current.getHours() + 1);
          break;
        case 'day':
          key = new Date(current.getFullYear(), current.getMonth(), current.getDate()).toISOString();
          current.setDate(current.getDate() + 1);
          break;
        case 'week':
          key = new Date(current.getFullYear(), current.getMonth(), current.getDate()).toISOString();
          current.setDate(current.getDate() + 7);
          break;
        case 'month':
          key = new Date(current.getFullYear(), current.getMonth(), 1).toISOString();
          current.setMonth(current.getMonth() + 1);
          break;
        default:
          key = new Date(current.getFullYear(), current.getMonth(), current.getDate()).toISOString();
          current.setDate(current.getDate() + 1);
      }
      buckets.set(key, { checkIns: 0, checkOuts: 0, date: new Date(key) });
    }

    return buckets;
  };

  const getBucketKey = (date, groupBy) => {
    const d = date instanceof Date ? date : date.toDate();
    switch (groupBy) {
      case 'hour':
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours()).toISOString();
      case 'day':
        return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
      case 'week':
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - d.getDay());
        return new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()).toISOString();
      case 'month':
        return new Date(d.getFullYear(), d.getMonth(), 1).toISOString();
      default:
        return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
    }
  };

  const generateReport = async () => {
    try {
      setGenerating(true);
      setError('');

      const { start, end } = getTimeframeRange(timeframe);
      const { groupBy, format } = getDateFormat(timeframe);

      // Query check-ins within the timeframe
      const checkInsRef = collection(db, 'checkIns');
      const q = query(
        checkInsRef,
        where('checkInTime', '>=', Timestamp.fromDate(start)),
        where('checkInTime', '<=', Timestamp.fromDate(end))
      );

      const querySnapshot = await getDocs(q);
      const buckets = generateTimeBuckets(start, end, groupBy);

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Filter by service category using new categorization logic
        if (!serviceMatchesFilter(data.serviceType, data.checkInTime, serviceFilter)) {
          return;
        }

        // Count check-ins
        const checkInKey = getBucketKey(data.checkInTime, groupBy);
        if (buckets.has(checkInKey)) {
          buckets.get(checkInKey).checkIns++;
        }

        // Count check-outs
        if (data.checkOutTime && data.status === 'checked_out') {
          const checkOutKey = getBucketKey(data.checkOutTime, groupBy);
          if (buckets.has(checkOutKey)) {
            buckets.get(checkOutKey).checkOuts++;
          }
        }
      });

      // Fetch additional attendance for the date range
      const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
      const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
      const additionalMap = new Map();
      try {
        const attendanceRef = collection(db, 'additionalAttendance');
        const attendanceQuery = query(
          attendanceRef,
          where(documentId(), '>=', startStr),
          where(documentId(), '<=', endStr)
        );
        const attendanceSnapshot = await getDocs(attendanceQuery);
        attendanceSnapshot.forEach(doc => {
          const data = doc.data();
          // doc.id is YYYY-MM-DD
          const [y, m, d] = doc.id.split('-').map(Number);
          const dateKey = new Date(y, m - 1, d);
          const bucketKey = getBucketKey(dateKey, groupBy);
          const existing = additionalMap.get(bucketKey) || 0;
          // Add sunday or tuesday count based on filter
          if (serviceFilter === 'all') {
            additionalMap.set(bucketKey, existing + (data.sundayCount || 0) + (data.tuesdayCount || 0));
          } else if (serviceFilter === 'Tuesday Prayer') {
            additionalMap.set(bucketKey, existing + (data.tuesdayCount || 0));
          } else {
            // Any Sunday service filter
            additionalMap.set(bucketKey, existing + (data.sundayCount || 0));
          }
        });
      } catch (err) {
        console.error('Error fetching additional attendance for timeline:', err);
      }

      // Convert to array and format for chart
      const chartData = Array.from(buckets.entries()).map(([key, bucket]) => ({
        date: format(bucket.date),
        'Check-Ins': bucket.checkIns,
        'Check-Outs': bucket.checkOuts,
        'Additional': additionalMap.get(key) || 0,
        'Total Attendance': bucket.checkIns + (additionalMap.get(key) || 0)
      }));

      setChartData(chartData);
    } catch (err) {
      console.error('Error generating timeline report:', err);
      setError('Failed to generate timeline report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const getTimeframeLabel = (tf) => {
    const labels = {
      day: 'Past 24 Hours',
      week: 'Past Week',
      month: 'Past Month',
      quarter: 'Past Quarter (3 Months)',
      'semi-annual': 'Past 6 Months',
      annual: 'Past Year (12 Months)'
    };
    return labels[tf] || tf;
  };

  const exportToCSV = () => {
    if (!chartData || !chartData.length) return;

    const headers = ['Date/Time', 'Check-Ins', 'Check-Outs', 'Additional', 'Total Attendance'];
    const rows = chartData.map(row => [
      row.date,
      row['Check-Ins'],
      row['Check-Outs'],
      row['Additional'],
      row['Total Attendance']
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    const serviceLabel = serviceFilter === 'all' ? 'all-services' : serviceFilter.replace(/\s+/g, '-');
    a.download = `timeline-report-${serviceLabel}-${timeframe}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    if (!chartData || !chartData.length) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to generate PDF');
      return;
    }

    const totalCheckIns = chartData.reduce((sum, item) => sum + item['Check-Ins'], 0);
    const totalCheckOuts = chartData.reduce((sum, item) => sum + item['Check-Outs'], 0);
    const totalAdditional = chartData.reduce((sum, item) => sum + (item['Additional'] || 0), 0);
    const totalAttendance = totalCheckIns + totalAdditional;
    const peakPeriod = chartData.reduce((max, item) =>
      item['Check-Ins'] > (max['Check-Ins'] || 0) ? item : max, {}
    );

    const serviceLabel = serviceFilter === 'all' ? 'all-services' : serviceFilter.replace(/\s+/g, '-');

    // Find max value for chart scaling
    const maxValue = Math.max(
      ...chartData.map(item => Math.max(item['Check-Ins'], item['Check-Outs']))
    );
    const chartHeight = 300;
    const chartWidth = 800;
    const padding = { top: 20, right: 30, bottom: 80, left: 50 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;

    // Generate SVG chart
    const xStep = plotWidth / (chartData.length - 1 || 1);
    const yScale = maxValue > 0 ? plotHeight / maxValue : 1;

    // Generate smooth curved path data using cubic Bézier curves
    const generateSmoothPath = (dataKey) => {
      const points = chartData.map((item, i) => ({
        x: padding.left + i * xStep,
        y: padding.top + plotHeight - (item[dataKey] * yScale)
      }));

      if (points.length === 0) return '';
      if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

      let path = `M ${points[0].x} ${points[0].y}`;

      for (let i = 0; i < points.length - 1; i++) {
        const current = points[i];
        const next = points[i + 1];

        // Calculate control points for smooth curve
        const tension = 0.3; // Smoothing factor (0-1, lower = smoother)
        const xDiff = (next.x - current.x) * tension;

        // Control point 1 (end of current point)
        const cp1x = current.x + xDiff;
        const cp1y = current.y;

        // Control point 2 (start of next point)
        const cp2x = next.x - xDiff;
        const cp2y = next.y;

        // Cubic Bézier curve
        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${next.x} ${next.y}`;
      }

      return path;
    };

    const checkInPath = generateSmoothPath('Check-Ins');
    const checkOutPath = generateSmoothPath('Check-Outs');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Timeline-Report-${serviceLabel}-${timeframe}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 15px;
            border-bottom: 2px solid #1a5f7a;
            padding-bottom: 10px;
          }
          .header h1 {
            color: #1a5f7a;
            margin: 0 0 5px 0;
            font-size: 22px;
          }
          .header p {
            margin: 0;
            font-size: 12px;
            color: #666;
          }
          .report-info {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            margin-bottom: 15px;
            padding: 12px;
            background: #f8fafc;
            border-radius: 6px;
          }
          .info-item {
            font-size: 12px;
          }
          .info-label {
            font-weight: bold;
            color: #64748b;
            display: inline-block;
            width: 100px;
          }
          .info-value {
            color: #1a5f7a;
            font-weight: 600;
          }
          .chart-container {
            margin: 20px 0;
            text-align: center;
          }
          .chart-svg {
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            background: white;
          }
          .legend {
            display: flex;
            justify-content: center;
            gap: 30px;
            margin: 15px 0;
            padding: 10px;
            background: #f8fafc;
            border-radius: 6px;
          }
          .legend-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            font-weight: 600;
          }
          .legend-line {
            width: 30px;
            height: 3px;
            border-radius: 2px;
          }
          .legend-line.green {
            background: #10b981;
          }
          .legend-line.crimson {
            background: #dc2626;
          }
          .summary-stats {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 10px;
            margin-bottom: 15px;
          }
          .stat-card {
            background: #f8fafc;
            padding: 12px;
            border-radius: 6px;
            border: 1px solid #e5e7eb;
            text-align: center;
          }
          .stat-label {
            font-size: 10px;
            color: #64748b;
            font-weight: bold;
            text-transform: uppercase;
            margin-bottom: 6px;
          }
          .stat-value {
            font-size: 20px;
            font-weight: bold;
            color: #1a5f7a;
          }
          .stat-value.green {
            color: #10b981;
          }
          .stat-value.crimson {
            color: #dc2626;
          }
          .footer {
            margin-top: 20px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #e5e7eb;
            padding-top: 10px;
          }
          @media print {
            body {
              margin: 15px;
            }
            @page {
              margin: 0.5in;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Timeline Report</h1>
          <p>AAR Guest Management System</p>
        </div>

        <div class="report-info">
          <div class="info-item">
            <span class="info-label">Timeframe:</span>
            <span class="info-value">${getTimeframeLabel(timeframe)}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Service:</span>
            <span class="info-value">${serviceFilter === 'all' ? 'All Services' : serviceFilter}</span>
          </div>
        </div>

        <div class="chart-container">
          <svg class="chart-svg" width="${chartWidth}" height="${chartHeight}" xmlns="http://www.w3.org/2000/svg">
            <!-- Grid lines -->
            ${[0, 1, 2, 3, 4].map(i => {
              const y = padding.top + (plotHeight / 4) * i;
              return `<line x1="${padding.left}" y1="${y}" x2="${chartWidth - padding.right}" y2="${y}" stroke="#e5e7eb" stroke-dasharray="3,3" />`;
            }).join('')}

            <!-- Y-axis labels -->
            ${[0, 1, 2, 3, 4].map(i => {
              const value = Math.round((maxValue / 4) * (4 - i));
              const y = padding.top + (plotHeight / 4) * i;
              return `<text x="${padding.left - 10}" y="${y + 4}" text-anchor="end" font-size="10" fill="#666">${value}</text>`;
            }).join('')}

            <!-- X-axis labels -->
            ${chartData.map((item, i) => {
              if (chartData.length > 20 && i % 2 !== 0) return '';
              const x = padding.left + i * xStep;
              const label = item.date.length > 15 ? item.date.substring(0, 12) + '...' : item.date;
              return `<text x="${x}" y="${chartHeight - padding.bottom + 15}" text-anchor="end" font-size="9" fill="#666" transform="rotate(-45 ${x} ${chartHeight - padding.bottom + 15})">${label}</text>`;
            }).join('')}

            <!-- Check-In line -->
            <path d="${checkInPath}" fill="none" stroke="#10b981" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
            ${chartData.map((item, i) => {
              const x = padding.left + i * xStep;
              const y = padding.top + plotHeight - (item['Check-Ins'] * yScale);
              return `<circle cx="${x}" cy="${y}" r="4" fill="#10b981" stroke="#10b981" stroke-width="1" />`;
            }).join('')}

            <!-- Check-Out line -->
            <path d="${checkOutPath}" fill="none" stroke="#dc2626" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
            ${chartData.map((item, i) => {
              const x = padding.left + i * xStep;
              const y = padding.top + plotHeight - (item['Check-Outs'] * yScale);
              return `<circle cx="${x}" cy="${y}" r="4" fill="#dc2626" stroke="#dc2626" stroke-width="1" />`;
            }).join('')}
          </svg>
        </div>

        <div class="legend">
          <div class="legend-item">
            <div class="legend-line green"></div>
            <span>Check-Ins</span>
          </div>
          <div class="legend-item">
            <div class="legend-line crimson"></div>
            <span>Check-Outs</span>
          </div>
        </div>

        <div class="summary-stats">
          <div class="stat-card">
            <div class="stat-label">Total Check-Ins</div>
            <div class="stat-value green">${totalCheckIns}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Additional Attendance</div>
            <div class="stat-value">${totalAdditional}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Attendance</div>
            <div class="stat-value green">${totalAttendance}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Total Check-Outs</div>
            <div class="stat-value crimson">${totalCheckOuts}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Peak Period</div>
            <div class="stat-value">${peakPeriod.date || 'N/A'}</div>
          </div>
        </div>

        <div class="footer">
          <p>Generated on ${new Date().toLocaleString()}</p>
          <p>AAR Guest Management System</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="loading"><div className="spinner"></div><p>Loading timeline report...</p></div>
    );
  }

  return (
    <div className="timeline-report-container">
      <div className="timeline-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Admin Panel
        </button>
        <h1 className="timeline-title">Timeline Report</h1>
      </div>

      {error && (
        <div className="message error-message">
          {error}
        </div>
      )}

      <div className="timeline-content">
        <div className="timeline-controls">
          <div className="control-group">
            <label htmlFor="timeframe">Select Timeframe:</label>
            <select
              id="timeframe"
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              className="timeframe-select"
            >
              <option value="day">Past 24 Hours</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="quarter">Past Quarter (3 Months)</option>
              <option value="semi-annual">Past 6 Months</option>
              <option value="annual">Past Year (12 Months)</option>
            </select>
          </div>

          <div className="control-group">
            <label htmlFor="serviceFilter">Filter by Service:</label>
            <select
              id="serviceFilter"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="service-select"
            >
              {services.map(service => (
                <option key={service} value={service}>
                  {service === 'all' ? 'All Services' : service}
                </option>
              ))}
            </select>
          </div>

          <button
            className="btn btn-primary"
            onClick={generateReport}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate Report'}
          </button>
        </div>

        {chartData && (
          <>
            <div className="chart-container">
              <div className="chart-header">
                <h2>Check-In & Check-Out Activity</h2>
                <div className="export-buttons">
                  <button
                    className="btn btn-export"
                    onClick={exportToCSV}
                  >
                    Export to CSV
                  </button>
                  <button
                    className="btn btn-pdf"
                    onClick={exportToPDF}
                  >
                    Generate PDF
                  </button>
                </div>
              </div>

              <div className="chart-info">
                <div className="info-item">
                  <span className="info-label">Timeframe:</span>
                  <span className="info-value">{getTimeframeLabel(timeframe)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Service:</span>
                  <span className="info-value">{serviceFilter === 'all' ? 'All Services' : serviceFilter}</span>
                </div>
                <div className="info-item stat-item">
                  <span className="info-label">Total Check-Ins:</span>
                  <span className="info-value green">{chartData.reduce((sum, item) => sum + item['Check-Ins'], 0)}</span>
                </div>
                <div className="info-item stat-item">
                  <span className="info-label">Additional Attendance:</span>
                  <span className="info-value">{chartData.reduce((sum, item) => sum + (item['Additional'] || 0), 0)}</span>
                </div>
                <div className="info-item stat-item">
                  <span className="info-label">Total Attendance:</span>
                  <span className="info-value green">
                    {chartData.reduce((sum, item) => sum + item['Check-Ins'] + (item['Additional'] || 0), 0)}
                  </span>
                </div>
                <div className="info-item stat-item">
                  <span className="info-label">Total Check-Outs:</span>
                  <span className="info-value crimson">{chartData.reduce((sum, item) => sum + item['Check-Outs'], 0)}</span>
                </div>
                <div className="info-item stat-item">
                  <span className="info-label">Peak Period:</span>
                  <span className="info-value">
                    {chartData.reduce((max, item) =>
                      item['Check-Ins'] > (max['Check-Ins'] || 0) ? item : max, {}
                    ).date || 'N/A'}
                  </span>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="date"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fontSize: 12, fill: '#666' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#666' }}
                    label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fill: '#666' } }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '10px'
                    }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="line"
                  />
                  <Line
                    type="monotone"
                    dataKey="Check-Ins"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ fill: '#10b981', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Check-Outs"
                    stroke="#dc2626"
                    strokeWidth={3}
                    dot={{ fill: '#dc2626', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Additional"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ fill: '#f59e0b', r: 3 }}
                    activeDot={{ r: 5 }}
                    strokeDasharray="5 5"
                  />
                  <Line
                    type="monotone"
                    dataKey="Total Attendance"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ fill: '#3b82f6', r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TimelineReport;
